/**
 * Environment detection utility
 * Provides methods to detect different build environments
 */

/**
 * Check if running in development mode
 * @returns {boolean} True if in development
 */
export const isDevelopment = () => {
  return import.meta.env.DEV;
};

/**
 * Check if running in production mode
 * @returns {boolean} True if in production
 */
export const isProduction = () => {
  return import.meta.env.PROD;
};

/**
 * Check if performance dashboard should be accessible
 * Dashboard is only accessible in development and test builds, NOT in production
 * @returns {boolean} True if dashboard should be accessible
 */
export const isDashboardAccessible = () => {
  // Dashboard accessible in development mode
  if (isDevelopment()) {
    return true;
  }
  
  // Dashboard accessible if explicitly enabled via environment variable (for testing)
  if (import.meta.env.VITE_ENABLE_PERFORMANCE_DASHBOARD === 'true') {
    return true;
  }
  
  // Dashboard NOT accessible in production builds
  return false;
};

/**
 * Async version that can check Electron environment variables (including from .env file)
 * @returns {Promise<boolean>} True if dashboard should be accessible
 */
export const isDashboardAccessibleAsync = async () => {
  // Dashboard accessible in development mode
  if (isDevelopment()) {
    return true;
  }
  
  // Dashboard accessible if explicitly enabled via environment variable (for testing)
  if (import.meta.env.VITE_ENABLE_PERFORMANCE_DASHBOARD === 'true') {
    return true;
  }
  
  // For Electron builds, check environment variables from main process (including .env file)
  if (isElectron() && typeof window !== 'undefined' && window.electronAPI?.getDashboardEnv) {
    try {
      const envVars = await window.electronAPI.getDashboardEnv();
      return envVars.VITE_ENABLE_PERFORMANCE_DASHBOARD === 'true';
    } catch (error) {
      console.warn('Could not get dashboard environment from Electron:', error);
    }
  }
  
  // Dashboard NOT accessible in production builds
  return false;
};

/**
 * Check if running in Electron
 * @returns {boolean} True if in Electron environment
 */
export const isElectron = () => {
  return import.meta.env.VITE_ELECTRON === 'true' || 
         (typeof window !== 'undefined' && window.electronAPI);
};

/**
 * Get current environment info for debugging
 * @returns {object} Environment information
 */
export const getEnvironmentInfo = () => {
  return {
    isDevelopment: isDevelopment(),
    isProduction: isProduction(),
    isElectron: isElectron(),
    isDashboardAccessible: isDashboardAccessible(),
    nodeEnv: import.meta.env.MODE,
    viteElectron: import.meta.env.VITE_ELECTRON,
    viteDashboardEnabled: import.meta.env.VITE_ENABLE_PERFORMANCE_DASHBOARD
  };
};
